import { useState, useCallback, useMemo } from 'react';

/**
 * Language/localization hook for the technician app.
 * Supports Hebrew (RTL), Hindi, and English.
 */

const LANGUAGES = {
  'he-IL': { name: 'Hebrew', nativeName: 'עברית', dir: 'rtl', flag: '🇮🇱' },
  'hi-IN': { name: 'Hindi', nativeName: 'हिन्दी', dir: 'ltr', flag: '🇮🇳' },
  'en-US': { name: 'English', nativeName: 'English', dir: 'ltr', flag: '🇺🇸' },
};

// UI strings in all 3 languages
const STRINGS = {
  'he-IL': {
    startDay: 'התחל יום עבודה',
    tasks: 'משימות',
    safety: 'בטיחות',
    profile: 'פרופיל',
    selectProject: 'בחר פרויקט',
    selectName: 'בחר את שמך',
    goodMorning: 'בוקר טוב',
    tasksToday: 'משימות להיום',
    critical: 'קריטי',
    high: 'גבוה',
    normal: 'רגיל',
    low: 'נמוך',
    start: 'התחל',
    complete: 'סיים',
    escalate: 'העבר למנהל',
    robot: 'רובוט',
    row: 'שורה',
    group: 'קבוצה',
    minutes: 'דקות',
    hours: 'שעות',
    endDay: 'סיום יום',
    completed: 'הושלם',
    remaining: 'נותר',
    uploadPhoto: 'צלם תמונה',
    speak: 'הקרא',
    listen: 'דבר',
    equipment: 'ציוד נדרש',
    repairGuide: 'מדריך תיקון',
    step: 'שלב',
    findings: 'ממצאים',
    partsReplaced: 'חלפים שהוחלפו',
    notes: 'הערות',
    weather: 'מזג אוויר',
    temperature: 'טמפרטורה',
    wind: 'רוח',
    safeToWork: 'בטוח לעבוד',
    dangerStop: 'סכנה - עצור עבודה',
    logout: 'התנתק',
    loading: 'טוען...',
    error: 'שגיאה',
    retry: 'נסה שוב',
    noTasks: 'אין משימות להיום',
    selectLanguage: 'בחר שפה',
  },
  'hi-IN': {
    startDay: 'दिन शुरू करें',
    tasks: 'कार्य',
    safety: 'सुरक्षा',
    profile: 'प्रोफ़ाइल',
    selectProject: 'प्रोजेक्ट चुनें',
    selectName: 'अपना नाम चुनें',
    goodMorning: 'सुप्रभात',
    tasksToday: 'आज के कार्य',
    critical: 'गंभीर',
    high: 'उच्च',
    normal: 'सामान्य',
    low: 'कम',
    start: 'शुरू',
    complete: 'पूरा',
    escalate: 'प्रबंधक को भेजें',
    robot: 'रोबोट',
    row: 'पंक्ति',
    group: 'समूह',
    minutes: 'मिनट',
    hours: 'घंटे',
    endDay: 'दिन समाप्त',
    completed: 'पूरा हुआ',
    remaining: 'शेष',
    uploadPhoto: 'फ़ोटो लें',
    speak: 'बोलें',
    listen: 'सुनें',
    equipment: 'आवश्यक उपकरण',
    repairGuide: 'मरम्मत गाइड',
    step: 'चरण',
    findings: 'निष्कर्ष',
    partsReplaced: 'बदले गए पुर्जे',
    notes: 'टिप्पणियाँ',
    weather: 'मौसम',
    temperature: 'तापमान',
    wind: 'हवा',
    safeToWork: 'काम करना सुरक्षित',
    dangerStop: 'खतरा - काम बंद करें',
    logout: 'लॉग आउट',
    loading: 'लोड हो रहा...',
    error: 'त्रुटि',
    retry: 'पुनः प्रयास',
    noTasks: 'आज कोई कार्य नहीं',
    selectLanguage: 'भाषा चुनें',
  },
  'en-US': {
    startDay: 'Start Day',
    tasks: 'Tasks',
    safety: 'Safety',
    profile: 'Profile',
    selectProject: 'Select Project',
    selectName: 'Select Your Name',
    goodMorning: 'Good Morning',
    tasksToday: 'Tasks Today',
    critical: 'Critical',
    high: 'High',
    normal: 'Normal',
    low: 'Low',
    start: 'Start',
    complete: 'Complete',
    escalate: 'Escalate',
    robot: 'Robot',
    row: 'Row',
    group: 'Group',
    minutes: 'min',
    hours: 'hrs',
    endDay: 'End Day',
    completed: 'Completed',
    remaining: 'Remaining',
    uploadPhoto: 'Take Photo',
    speak: 'Speak',
    listen: 'Listen',
    equipment: 'Equipment Needed',
    repairGuide: 'Repair Guide',
    step: 'Step',
    findings: 'Findings',
    partsReplaced: 'Parts Replaced',
    notes: 'Notes',
    weather: 'Weather',
    temperature: 'Temperature',
    wind: 'Wind',
    safeToWork: 'Safe to Work',
    dangerStop: 'Danger - Stop Work',
    logout: 'Logout',
    loading: 'Loading...',
    error: 'Error',
    retry: 'Retry',
    noTasks: 'No tasks for today',
    selectLanguage: 'Select Language',
  },
};

export default function useLanguage(initialLang = 'en-US') {
  const [language, setLanguage] = useState(
    () => localStorage.getItem('tech_language') || initialLang
  );

  const changeLang = useCallback((lang) => {
    setLanguage(lang);
    localStorage.setItem('tech_language', lang);
  }, []);

  const info = LANGUAGES[language] || LANGUAGES['en-US'];
  const isRTL = info.dir === 'rtl';

  const t = useMemo(() => STRINGS[language] || STRINGS['en-US'], [language]);

  return {
    language,
    setLanguage: changeLang,
    isRTL,
    dir: info.dir,
    langInfo: info,
    t,
    languages: LANGUAGES,
  };
}
